import { HttpErrorResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { isEmpty, map as _map, orderBy, sortBy } from 'lodash'
import { BehaviorSubject, Observable, of, throwError } from 'rxjs'
import { catchError, distinctUntilChanged, filter, map, shareReplay, switchMap } from 'rxjs/operators'

import { environment } from '../../../../environments'
import { type SelectOption } from '../classes'
import { AppointmentStatuses, NOT_FOUND } from '../data'
import { IDispute, IPayout } from '../interfaces'
import { Appointment } from '../model/appointment'
import { Clinic, ClinicData, PaymentProvider } from '../model/clinic'
import { Clinician, ClinicianData } from '../model/clinician'
import { Facility, FacilityData } from '../model/facility'
import { IntegrationLog } from '../model/integration'
import { ProductData, ProductModel } from '../model/product'
import { EmbeddedService } from './embedded.service'
import { PagedRestResponse, RestResponse, RestService } from './rest/'

export const CLINIC_STORAGE_KEY = 'ppal-last-clinic'

type StatusMap = SelectOption<Appointment.STATUS | string>[]

@Injectable({
  providedIn: 'root',
})
export class ClinicService {
  static clinic: Clinic
  static clinicId: string

  get clinic(): Clinic {
    return ClinicService.clinic
  }

  get clinicId() {
    if (ClinicService.clinic != null) {
      return ClinicService.clinic.id
    } else {
      return localStorage.getItem(CLINIC_STORAGE_KEY)
    }
  }

  // Pure: no static reads, no clinic$ subscribes. Callers pass the clinic they hold.
  isUnifiedEstimatorEnabled(clinic: Clinic | null): boolean {
    return clinic?.pricer?.enable === true && clinic?.modulesPreferences?.unifiedEstimator === true
  }

  clinicSlug$ = new BehaviorSubject<{ slug: string, reload?: boolean }>(null)
  clinicId$ = new BehaviorSubject<string>(null)

  clinic$: Observable<Clinic | null> = this.clinicSlug$.pipe(
    filter(Boolean),
    /**
     * Avoid reloading if slug didn't change
     * But allow reloads when explicitly requested
     */
    distinctUntilChanged((a, b) => b.reload !== true && a.slug === b.slug),
    switchMap(({ slug }) => this.restService.get<ClinicData>(`/clinics/${slug}`)),
    map(result => {
      ClinicService.clinic = new Clinic(result.data)
      ClinicService.clinicId = result.data.id
      localStorage.setItem(CLINIC_STORAGE_KEY, ClinicService.clinic.id)
      this.clinicId$.next(ClinicService.clinicId)
      return ClinicService.clinic
    }),
    catchError((err: HttpErrorResponse) => {
      // if the clinic request returned a 404, then show an error to the user
      if (err.status === NOT_FOUND) {
        // window.location.href = 'https://www.patientpal.com/login.html?clinic-not-found=1'
        return of(null)
      } else {
        return throwError(() => err)
      }
    }),
    shareReplay(1),
  )

  private cache: {
    facilities: Facility[] | null
    userFacilities: Facility[] | null
  } = {
      facilities: null,
      userFacilities: null,
    }

  /** Shared per catalog shape so the selectors on a page issue one request between them */
  private productsCache: Record<string, Observable<ProductModel[]>> = {}

  constructor(
    protected restService: RestService,
    embedded: EmbeddedService,
  ) {
    if (!embedded.isEmbedded) {
      this.detectSlug()
    }
  }

  private detectSlug() {
    let idOrSlug = environment['clinicId']

    if (localStorage.getItem(CLINIC_STORAGE_KEY)) {
      idOrSlug = localStorage.getItem(CLINIC_STORAGE_KEY)
      ClinicService.clinicId = idOrSlug
      this.clinicId$.next(idOrSlug)
    }

    if (!idOrSlug) {
      const host = location.hostname
      const slug = host.split('.')[0]

      if (host.includes('localhost') && environment.env === 'qa') {
        this.clinicSlug$.next({ slug })
        return
      }

      const blacklistedSubdomains = ['console']

      if (!blacklistedSubdomains.includes(slug)) {
        idOrSlug = host
      }
    }

    if (idOrSlug) {
      this.clinicSlug$.next({ slug: idOrSlug })
    }
  }

  /**
   * Appointment statuses for the dropdown filter
   * If a clinic has a custom status map, uses that instead
   */
  appointmentStatuses$: Observable<StatusMap> = this.clinic$.pipe(
    map(clinic => {
      if (isEmpty(clinic.settings?.emrStatusMap)) {
        return AppointmentStatuses
      } else {
        return orderBy(Object.keys(clinic.settings.emrStatusMap).map((status) => ({
          value: status,
          viewValue: status,
        })), 'viewValue')
      }
    }),
  )

  getPaymentProvider(): Observable<PaymentProvider | undefined> {
    const route = `/clinics/${this.clinicId}/payment-provider`
    return this.restService.get<PaymentProvider>(route).pipe(
      map(response => response.data),
      catchError((err: HttpErrorResponse) => {
        // just return void if we get a 404, this means there is no payment provider setup for the clinic yet
        if (err.status === NOT_FOUND) {
          return of()
        } else {
          return throwError(() => err)
        }
      }),
    )
  }

  updatePaymentProvider(paymentProvider: PaymentProvider) {
    return this.restService.patch<void>(
      `/clinics/${this.clinicId}/payment-provider`,
      paymentProvider,
    )
  }

  getClinicians(): Observable<Clinician[]> {
    const route = '/clinicians'
    return this.restService.get<ClinicianData[]>(route).pipe(
      map((response) => {
        const clinicians: Clinician[] = _map(
          response.data,
          (clinicianData) => new Clinician(clinicianData),
        )
        return clinicians
      }),
    )
  }

  getProducts(input?: {
    prices?: boolean
    ids?: string | string[]
    carrierLookup?: string
    query?: string
    /** Load the procedures the source stopped offering too, so a saved one still resolves */
    includeInactive?: boolean
  }, bustCache = false): Observable<ProductModel[]> {
    // only the unfiltered catalog is worth caching; the other shapes are one-off lookups
    const cacheKey = input?.ids == null && !input?.prices && !input?.query && !input?.carrierLookup
      ? `includeInactive:${input?.includeInactive === true}`
      : null

    if (cacheKey != null && !bustCache && this.productsCache[cacheKey] != null) {
      return this.productsCache[cacheKey]
    }

    const route = `/clinics/${this.clinicId}/products`
    const params = this.restService.convertToParams(input)
    const request = this.restService.get<ProductData[]>(route, params).pipe(
      map((resp) => {
        if (resp.success) {
          return sortBy(
            _map(resp.data, (productData) => new ProductModel(productData)),
            'name',
          )
        } else {
          return []
        }
      }),
    )

    if (cacheKey == null) {
      return request
    }

    const shared = request.pipe(
      catchError((err) => {
        delete this.productsCache[cacheKey]
        return throwError(() => err)
      }),
      shareReplay(1),
    )

    this.productsCache[cacheKey] = shared
    return shared
  }

  clearProductsCache() {
    this.productsCache = {}
  }

  getIntegrationLogs(
    input: any,
  ): Observable<PagedRestResponse<IntegrationLog[]>> {
    const route = `/integration/logs`
    const params = this.restService.convertToParams(input)
    return this.restService.get<any[]>(route, params)
  }

  getIntegrationQueue(input: {
    limit?: number
    startAt?: string
  }): Observable<RestResponse<any>> {
    const route = `/integration/queue`
    const params = this.restService.convertToParams(input)
    return this.restService.get<any[]>(route, params)
  }

  getLocations(
    input?: {
      visibleForClinician?: boolean
      includeDisabled?: boolean
    },
    bustCache = false,
  ): Observable<Facility[]> {
    if (
      input?.visibleForClinician &&
      this.cache.userFacilities != null &&
      !bustCache
    ) {
      return of(this.cache.userFacilities)
    } else if (
      !input?.visibleForClinician &&
      this.cache.facilities != null &&
      !bustCache
    ) {
      return of(this.cache.facilities)
    }
    const route = `/clinics/${this.clinicId}/locations`
    const params = this.restService.convertToParams(input)
    return this.restService.get<FacilityData[]>(route, params).pipe(
      map((response) => {
        const locations = _map(
          response.data,
          (location) => new Facility(location),
        )
        if (input?.visibleForClinician) {
          this.cache.userFacilities = locations
        } else {
          this.cache.facilities = locations
        }
        return locations
      }),
    )
  }

  getMetadataFields(): Observable<string[]> {
    const route = `/clinics/${this.clinicId}/metadata-fields`
    return this.restService.get<string[]>(route).pipe(map((resp) => resp.data))
  }

  getRecordLabels(): Observable<Record<string, string>> {
    const route = `/clinics/${this.clinicId}/record-labels`
    return this.restService.get<Record<string, string>>(route).pipe(map((resp) => resp.data))
  }

  getPayouts(input?: { arrivalDate: string }) {
    const route = `/payments/payouts`
    const params = this.restService.convertToParams(input)
    return this.restService.get<IPayout[]>(route, params)
  }

  getDisputes(input?: { arrivalDate: string }) {
    const route = `/payments/disputes`
    const params = this.restService.convertToParams(input)
    return this.restService.get<IDispute[]>(route, params)
  }

  reloadClinic() {
    const current = this.clinicSlug$.getValue()
    if (current) {
      this.clinicSlug$.next({ slug: current.slug, reload: true })
    }
  }
}
